import { useEffect, useRef } from 'react';
import { AccessibilityInfo, Platform, UIManager, findNodeHandle } from 'react-native';

/**
 * A hook that will set screen reader focus on mount of whichever component you pass the returned ref into
 *
 * Usage:
 * ```ts
 * const MyComponent = () => {
 *   const ref = useAccessibilityFocus(true);
 *   return (
 *     <Text ref={ref}>
 *       Some Text
 *     </Text>
 *   )
 * }
 * ```
 *
 * @param autofocus controls whether the focus code will run.
 * Allows for use of this hook in generic components like H1 where you don't always want to focus.
 */
export const useAccessibilityFocus = (autofocus?: boolean) => {
  const ref = useRef<any>(null);

  useEffect(() => {
    if (!autofocus || !ref.current) return;

    if (Platform.OS === 'web') {
      try {
        // focus using react-native-web's recommended technique.
        // Unfortunately this crashes on native, so we're only doing it in web.
        // https://necolas.github.io/react-native-web/docs/accessibility/#keyboard-focus
        // @ts-expect-error TS doesn't know about the focus method, but it is there
        UIManager.focus(ref.current);
      } catch (e) {
        console.warn('Failed to set accessibility focus on web:', e);
      }
    } else {
      // For Android and iOS, try to directly use the native node
      const nodeHandle = findNodeHandle(ref.current);
      if (nodeHandle !== null) {
        AccessibilityInfo.setAccessibilityFocus(nodeHandle);
      }
    }
  }, [autofocus]);

  return ref;
};
